import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Tooltip,
  Stack,
  Divider,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  MedicalServices as MedicalIcon,
  Gavel as LegalIcon,
  Computer as TechnicalIcon,
  History as HistoryIcon,
  Business as BusinessIcon,
  School as AcademicIcon,
  Chat as GeneralIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

// Icon mapping for domain types
const domainIcons = {
  general: <GeneralIcon />,
  medical: <MedicalIcon />,
  legal: <LegalIcon />,
  technical: <TechnicalIcon />,
  history: <HistoryIcon />,
  business: <BusinessIcon />,
  academic: <AcademicIcon />,
};

// Color mapping for domain types
const domainColors = {
  general: '#2196F3',
  medical: '#F44336',
  legal: '#9C27B0',
  technical: '#4CAF50',
  history: '#FF9800',
  business: '#00BCD4',
  academic: '#795548',
};

function DomainTemplateList() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPanel, setExpandedPanel] = useState(false);
  const [copiedPrompt, setCopiedPrompt] = useState(null);

  useEffect(() => {
    loadDomainTemplates();
  }, []);

  const loadDomainTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getDomains();
      setTemplates(data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load domain templates:', err);
      setError('Failed to load domain templates. Please try again later.');
      setLoading(false);
    }
  };

  const handleAccordionChange = (panel) => (_event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  const handleCopyPrompt = async (templateId, prompt) => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedPrompt(templateId);
      setTimeout(() => setCopiedPrompt(null), 2000);
    } catch (err) {
      console.error('Failed to copy prompt:', err);
    }
  };

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.system_prompt.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Loading domain templates...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" paragraph>
        Domain templates provide specialized system prompts optimized for different fields.
        These templates are used when transcribing to improve accuracy for domain-specific terminology.
      </Typography>

      <TextField
        fullWidth
        placeholder="Search templates..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {filteredTemplates.length === 0 && (
        <Alert severity="info">
          No templates match your search query.
        </Alert>
      )}

      {filteredTemplates.map((template) => (
        <Accordion
          key={template.id}
          expanded={expandedPanel === template.id}
          onChange={handleAccordionChange(template.id)}
          sx={{
            mb: 2,
            '&:before': { display: 'none' },
            boxShadow: 1,
            '&.Mui-expanded': {
              boxShadow: 3,
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              borderLeft: 4,
              borderColor: domainColors[template.id] || '#2196F3',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Box sx={{ color: domainColors[template.id] || '#2196F3' }}>
                {domainIcons[template.id] || <GeneralIcon />}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">{template.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {template.description}
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <Stack spacing={3}>
              {/* System Prompt Section */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    System Prompt
                  </Typography>
                  <Tooltip title={copiedPrompt === template.id ? 'Copied!' : 'Copy to clipboard'}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyPrompt(template.id, template.system_prompt)}
                      color={copiedPrompt === template.id ? 'success' : 'default'}
                    >
                      {copiedPrompt === template.id ? <CheckCircleIcon /> : <ContentCopyIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: 'grey.50',
                    border: 1,
                    borderColor: 'grey.300',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: '100%',
                    overflow: 'auto',
                  }}
                >
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      m: 0,
                      fontFamily: 'inherit',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                    }}
                  >
                    {template.system_prompt}
                  </Typography>
                </Paper>
              </Box>

              <Divider />

              {/* Example Vocabulary Section */}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Example Vocabulary ({template.example_vocabulary.length} terms)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {template.example_vocabulary.map((term, index) => (
                    <Chip
                      key={index}
                      label={term}
                      size="small"
                      sx={{
                        backgroundColor: `${domainColors[template.id]}15`,
                        color: domainColors[template.id],
                        borderColor: domainColors[template.id],
                        border: 1,
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Usage Note */}
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>How to use:</strong> Select the "{template.name}" domain when creating or editing a project.
                  This template will be automatically applied during transcription.
                </Typography>
              </Alert>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

export default DomainTemplateList;
